import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import CustomSelect from "./ui/CustomSelect";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const Staff = () => {
  const [staff, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [loading, setLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const navigate = useNavigate();

  const fetchClients = async () => {
    try {
      setClientsLoading(true);
      setLoading(true);
      setErrorMessage("");
      const endpoint = `/get-all-clients?page=1&per_page=1000&search=&sort_by=client_id&sort_order=desc`;
      const response = await apiCall("get", endpoint, null, navigate);

      if (response?.data?.success) {
        const clientList = response.data.data || [];
        setClients(clientList);
      } else {
        console.error("Failed to load clients.");
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
      setErrorMessage(true);
    } finally {
      setClientsLoading(false);
    }
  };

  const fetchStaff = async (
    page = 1,
    perPage = 10,
    search = "",
    clientId = "",
    sortBy = null,
    sortOrder = "asc"
  ) => {
    try {
      setLoading(true);
      setErrorMessage("");

      let endpoint = `/get-all-staff-bo?page=${page}&per_page=${perPage}`;

      if (search.trim()) {
        endpoint += `&search=${encodeURIComponent(search.trim())}`;
      }
      if (clientId) {
        endpoint += `&client_id=${encodeURIComponent(clientId)}`;
      }
      if (sortBy) {
        endpoint += `&sort_by=${sortBy}&sort_order=${sortOrder}`;
      }

      const response = await apiCall("get", endpoint, null, navigate);

      if (response?.data?.success) {
        const staffList = (response.data.data || []).map((member) => ({
          ...member,
          display_created_datetime: member.created_datetime
            ? new Date(member.created_datetime).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })
            : "-",
        }));
        const pagination = response.data.pagination || {};
        const total = pagination.total || staffList.length;
        const currentPageFromResponse = pagination.current_page || page;
        const perPageFromResponse = pagination.per_page || perPage;
        const lastPage =
          pagination.last_page || Math.ceil(total / perPage) || 1;

        setStaff(staffList);
        setFilteredStaff(staffList);
        setTotalEntries(total);
        setCurrentPage(parseInt(currentPageFromResponse, 10));
        setPerPage(parseInt(perPageFromResponse, 10));
        setTotalPages(parseInt(lastPage, 10));
      } else {
        setErrorMessage("Failed to load staff.");
        setStaff([]);
        setFilteredStaff([]);
        setTotalEntries(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
      setErrorMessage(error.message || "Error fetching staff.");
      setStaff([]);
      setFilteredStaff([]);
      setTotalEntries(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debouncedFetchStaff = useCallback(
    debounce((page, perPage, search, clientId, sortBy, sortOrder) => {
      fetchStaff(page, perPage, search, clientId, sortBy, sortOrder);
    }, 500),
    []
  );

  // Fetch clients only once when the component mounts
  useEffect(() => {
    fetchClients();
  }, []);

  // Fetch staff when dependencies change
  useEffect(() => {
    debouncedFetchStaff(
      currentPage,
      perPage,
      searchTerm,
      selectedClient,
      sortConfig.field,
      sortConfig.direction
    );
  }, [
    currentPage,
    perPage,
    sortConfig.field,
    sortConfig.direction,
    searchTerm,
    selectedClient,
    debouncedFetchStaff,
  ]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleClientChange = (selected) => {
    setSelectedClient(selected?.value || "");
    setCurrentPage(1);
  };

  const handleEntriesPerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    const newDirection =
      sortConfig.field === field && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({
      field,
      direction: newDirection,
    });
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const sortedStaff = [...filteredStaff].sort((a, b) => {
    if (!sortConfig.field) return 0;
    const valueA = a[sortConfig.field] || "";
    const valueB = b[sortConfig.field] || "";
    if (sortConfig.field === "is_active") {
      const valA = valueA ? "Active" : "Inactive";
      const valB = valueB ? "Active" : "Inactive";
      return sortConfig.direction === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA); // Fixed typo here
    }
    if (sortConfig.field === "created_datetime") {
      const dateA = valueA ? new Date(valueA).getTime() : 0;
      const dateB = valueB ? new Date(valueB).getTime() : 0;
      return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
    }
    return sortConfig.direction === "asc"
      ? valueA.toString().localeCompare(valueB.toString())
      : valueB.toString().localeCompare(valueA.toString());
  });

  const startEntry = totalEntries > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const endEntry = Math.min(currentPage * perPage, totalEntries);

  if (errorMessage) {
    return <ErrorAlert />;
  }
  return (
    <div className="p-4 p-md-3 p-sm-2">
      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div
              className="icon-field flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by staff name..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={perPage}
              handlePerPageChange={handleEntriesPerPageChange}
            />
            <div style={{ minWidth: "200px" }}>
              <CustomSelect
                name="client"
                value={selectedClient}
                onChange={handleClientChange}
                options={clients}
                placeholder="Select Client"
                labelKey="client_name"
                valueKey="client_id"
                isLoading={clientsLoading}
                isClearable={true}
                isSearchable={true}
                noOptionsMessage={() => "No clients found"}
              />
            </div>
          </div>

          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              ></button>
            </div>
          )}

          {sortedStaff.length === 0 && !loading ? (
            <TableDataNoAvailable message="No staff found." />
          ) : (
            <div className="position-relative">
              <div
                className="table-responsive"
                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
              >
                <table className="table bordered-table mb-0 table-hover">
                  <thead>
                    <tr style={{ backgroundColor: "#475EAB", color: "white" }}>
                      <th scope="col" className="p-2">
                        <span>S.No</span>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("staff_name")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Staff Name</span>
                          <SortIcons
                            field="staff_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("company_name")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Company Name</span>
                          <SortIcons
                            field="company_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("client_name")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Client Name</span>
                          <SortIcons
                            field="client_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("staff_email")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Staff Email</span>
                          <SortIcons
                            field="staff_email"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("staff_phone")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Staff Phone</span>
                          <SortIcons
                            field="staff_phone"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("staff_role")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Staff Role</span>
                          <SortIcons
                            field="staff_role"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("created_datetime")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created Date</span>
                          <SortIcons
                            field="created_datetime"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("is_active")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex ">
                          <span>Status</span>
                          <SortIcons
                            field="is_active"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedStaff.map((member, index) => (
                      <tr key={member.staff_id || index}>
                        <td className="p-2">
                          {(currentPage - 1) * perPage + index + 1}
                        </td>
                        <td className="p-2">{member.staff_name || "-"}</td>
                                               <td className="p-2">{member.company_name || "-"}</td>

                        <td className="p-2">
                          <Link
                            to={`/all-clients`}
                            className="text-primary-600"
                          >
                            {member.client_name || "-"}
                          </Link>
                        </td>
                        <td className="p-2">{member.staff_email || "-"}</td>
                        <td className="p-2">{member.staff_phone || "-"}</td>
                        <td className="p-2">{member.staff_role || "-"}</td>
                        <td className="p-2">
                          {member.display_created_datetime}
                        </td>
                        <td className="p-2">
                          <span
                            className={`px-3 py-1 radius-4 fw-medium text-sm ${
                              member.is_active
                                ? "bg-success-focus text-success-600 border border-success-main"
                                : "bg-danger-focus text-danger-600 border border-danger-main"
                            }`}
                          >
                            {member.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {loading && (
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                  style={{
                    zIndex: 10,
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <LoadingComponent />
                </div>
              )}
              <div className="">
                <PaginationControls
                  pagination={{
                    currentPage,
                    perPage,
                    total: totalEntries,
                  }}
                  handlePageChange={handlePageChange}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Staff;
